import React from 'react';
import styled from 'styled-components';

import { NodeAIArgument } from '@components/Flow/Nodes';
import { Stack, Switch, TextField } from '@mui/material';
import { Select } from '@common/Select';
import { Option } from '@common/BucketInput/BucketField';
import { Label } from '@common/Label';
import { Tooltip } from '@common/Tooltip';
import { InfoOutlined } from '@mui/icons-material';
import { SubText } from '@common/SubText/SubText';
import { useTheme } from '../../../../../../../../styles';
import { Expression } from '@common/Expression';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  & .MuiInputBase-input {
    height: 24px;
  }
`;

interface Props {
  toolArgument: NodeAIArgument;
  setToolArguments: React.Dispatch<React.SetStateAction<NodeAIArgument[]>>;
}

const Notice = styled.p<{ error?: boolean }>`
  font-size: 12px;
  color: ${({ theme, error }) => (error ? theme.telnyx.red : theme.text.subtext)};
  padding-left: 12px;
`;

export const AssistantToolArgument: React.FC<Props> = ({ toolArgument, setToolArguments }) => {
  const theme = useTheme();
  const required = typeof toolArgument.required === 'undefined' ? true : toolArgument?.required === 'true';

  const updateArgument = (field: keyof NodeAIArgument, value: string) => {
    setToolArguments((prevState) =>
      prevState.map((existingArg) => {
        if (existingArg.id === toolArgument.id) {
          if (field === 'enumerator') {
            const enums = value.split(',');
            const enumFormatted = [];

            for (const item of enums) {
              enumFormatted.push(item.replace(/\n/g, ' ').trim());
            }

            value = enumFormatted.join(',\n');
          }

          return {
            ...existingArg,
            [field]: value,
          };
        }

        return existingArg;
      })
    );
  };

  const deleteArgument = () => {
    setToolArguments((prevState) => prevState.filter((item) => item.id !== toolArgument.id));
  };

  const searchTypeOptions: Option[] = [
    { label: 'string', value: 'string' },
    { label: 'boolean', value: 'boolean' },
    { label: 'enum', value: 'enum' },
    { label: 'number', value: 'Number' },
  ];

  const argumentOptions = {
    datetime: {
      iso: 'string',
    },
  };

  return (
    <Container>
      <Stack direction='row' justifyContent='space-between'>
        <Label
          title={'Argument'}
          tooltip='An argument generated by the LLM when the tool is executed.'
          onDelete={deleteArgument}
        />
        <Stack alignItems='center' spacing={0.75} direction='row'>
          <Tooltip
            sx={{ maxWidth: '300px' }}
            placement='top'
            arrow
            title='This determines if the AI is required to output this argument or not. If not required, the AI will sometimes fill in a dummy value for this argument.'
          >
            <Stack direction='row' spacing={0.75} sx={{ '&:hover': { cursor: 'pointer' } }}>
              <InfoOutlined sx={{ fontSize: '16px', color: theme.text.subtext }} />
              <SubText>Required</SubText>
            </Stack>
          </Tooltip>
          <Switch
            size='small'
            checked={required}
            onChange={(e) => updateArgument('required', e?.target?.checked?.toString())}
          />
        </Stack>
      </Stack>
      <Stack display='grid' gridTemplateColumns='6fr 4fr' gap='8px'>
        <TextField
          fullWidth
          size='small'
          value={toolArgument.name}
          // error={fieldError?.id === 'search_descriptor'}
          // helperText={fieldError?.id === 'search_descriptor' ? fieldError.error : undefined}
          onChange={(e) => updateArgument('name', e.target.value)}
          placeholder='Argument Name'
        />
        <Select
          value={toolArgument.type}
          setValue={(value) => updateArgument('type', value)}
          options={searchTypeOptions}
          placeholder='Select an index search type'
        />
      </Stack>
      <Expression
        key={`description`}
        keyProp={`description`}
        value={toolArgument.description}
        title={'Argument Description'}
        description={
          'A description of how the LLM should generate this argument. You can add the datetime in ISO format as a dynamic variable.'
        }
        setValue={(value: string) => updateArgument('description', value)}
        options={argumentOptions}
        placeholder='Description'
      />
      {toolArgument.type === 'enum' && (
        <TextField
          fullWidth
          size='small'
          value={toolArgument.enumerator}
          // error={fieldError?.id === 'search_descriptor'}
          // helperText={fieldError?.id === 'search_descriptor' ? fieldError.error : undefined}
          onChange={(e) => updateArgument('enumerator', e.target.value)}
          placeholder='Enums'
          multiline
        />
      )}
      {toolArgument.type === 'enum' && (
        <Notice error={toolArgument.enumerator?.length > 0 && !toolArgument.enumerator?.includes(',')}>
          {toolArgument.enumerator?.length > 0 && !toolArgument.enumerator?.includes(',')
            ? 'An enum type must have at least two values separated by a comma.'
            : 'Enumerators are separated using a comma (,)'}
        </Notice>
      )}
    </Container>
  );
};
